import { Card, DynamicLink, SectionWrapper } from "src/components/shared";
import BgGradient from "src/images/Ellipse_2047.png";
import BgGradient2 from "src/images/Ellipse_2048.png";
import BgGradient3 from "src/images/Ellipse_2049.png";
import { FaArrowRightLong } from "react-icons/fa6";
import { useState, useRef, useEffect } from "react";
import { highlightBracketedText } from "src/utils/utils";

type VerticalTab = {
  tabTitle: string;
  featureCardTitle: string;
  featureCardDescription: string;
  featureCardImage: { url: string, title:string };
  featureCardLinkText: string;
  featureCardLinkUrl: string;
  card1Title: string;
  card2Title: string;
  card3Title: string;
  card4Title: string;
  card1Description: string;
  card2Description: string;
  card3Description: string;
  card4Description: string;
  card1Image: { url: string };
  card2Image: { url: string };
  card3Image: { url: string };
  card4Image: { url: string };
};

type CapabilityComponentProps = {
  content?: {
    title: string;
    linkButton: {
      buttonText: string;
      buttonLink: string;
    };
    verticalTabsCollection: {
      items: VerticalTab[];
    };
  };
};

const CapabilityComponent = ({ content }: CapabilityComponentProps) => {
  const [activeNav, setActiveNav] = useState("");
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const isManualScroll = useRef(false);

  const navItems = content?.verticalTabsCollection?.items.map((item) => item.tabTitle) || [];

  useEffect(() => {
    if (navItems.length > 0 && !activeNav) {
      setActiveNav(navItems[0]);
    }
  }, [navItems, activeNav]);

  const checkVisibility = (element: HTMLElement) => {
    const rect = element.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const topThreshold = windowHeight * 0.3; // 30% from the top
    const bottomThreshold = windowHeight * 0.7; // 70% from the top

    return rect.top <= topThreshold && rect.bottom >= topThreshold;
  };

  const handleScroll = () => {
    if (!cardRefs.current.length || isManualScroll.current) return;

    for (let i = 0; i < cardRefs.current.length; i++) {
      const card = cardRefs.current[i];
      if (card && checkVisibility(card)) {
        setActiveNav(navItems[i]);
        break;
      }
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navItems]);

  const scrollToCard = (index: number) => {
    const card = cardRefs.current[index];
    if (!card) return;

    isManualScroll.current = true;
    setActiveNav(navItems[index]);

    const offset = window.innerHeight * 0.3;
    const cardPosition = card.getBoundingClientRect().top + window.scrollY;
    const scrollTarget = cardPosition - offset;

    window.scrollTo({
      top: scrollTarget,
      behavior: 'smooth'
    });

    setTimeout(() => {
      isManualScroll.current = false;
    }, 1000);
  };

  if (!content) return null;

  return (
    <SectionWrapper className="grid-common-component-block capability pb-0" id="products">
      <div className="container-fluid custom-spacer-col" style={{ position: "relative"}}>
        <div className="main-content">
          {[BgGradient, BgGradient3, BgGradient2].map((gradient, index) => {
            const topPositions = [150, 550, 1300];
            return (
              <img
                key={`gradient${index + 1}`}
                src={gradient}
                style={{
                  zIndex: 1,
                  width: "100%",
                  height: "33.33%",
                  left: 0,
                  top: `${topPositions[index]}px`,
                  position: "absolute",
                }}
                alt={`gradient${index + 1}`}
              />
            );
          })}
           <div className="title-content">
            <span
              className="highlight-bracket-text"
              dangerouslySetInnerHTML={{
                __html: highlightBracketedText(content.title, "bold") ?? "",
              }}
            />
            <div style={{ marginTop: "20px", marginBottom: "20px" }}>
              <DynamicLink url={content.linkButton?.buttonLink} className="btn-white">
                <span>{content.linkButton?.buttonText}</span>
                <FaArrowRightLong className="btn-icon" />
              </DynamicLink>
            </div>
          </div>

          <div className="container-fluid" style={{ zIndex: 3, marginTop: "100px" }}>
         
            <div className="capability-wrapper">
              <div className="side-navigation" style={{ position: "sticky", top: "20px" }}>
                {navItems.map((item, index) => (
                  <div
                    key={item}
                    className={`nav-item ${activeNav === item ? "active" : ""}`}
                    onClick={() => scrollToCard(index)}
                    style={{ cursor: "pointer" }}
                  >
                    {item}
                  </div>
                ))}
              </div>
              <div className="cards-container">
                {content.verticalTabsCollection.items.map((item, index) => (
                  <div
                    key={index}
                    ref={(el) => {
                      cardRefs.current[index] = el;
                    }}
                    style={{ scrollMarginTop: "20px" }}
                  >
                    <Card
                      item={item}
                      index={index}
                      typographyClassName="fw-bold mb-0"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default CapabilityComponent;